'use client';

import { createContext, useContext, useMemo } from 'react';
import { CssBaseline } from '@mui/material';
import { ThemeProvider, useColorScheme } from '@mui/material/styles';
import theme from '@/theme';

export type ThemePreference = 'system' | 'light' | 'dark';
export type EffectiveThemeMode = 'light' | 'dark';

export const themeStorageKey = 'torlin-web-theme';

const WebThemeContext = createContext<{
  preference: ThemePreference;
  effectiveMode: EffectiveThemeMode;
  setPreference: (preference: ThemePreference) => void;
}>({ preference: 'system', effectiveMode: 'light', setPreference: () => undefined });

function ThemeContextBridge({ children }: { children: React.ReactNode }) {
  const { mode, systemMode, setMode } = useColorScheme();
  const preference: ThemePreference = mode ?? 'system';
  const effectiveMode: EffectiveThemeMode = preference === 'system' ? (systemMode ?? 'light') : preference;
  const value = useMemo(() => ({
    preference,
    effectiveMode,
    setPreference: (nextPreference: ThemePreference) => setMode(nextPreference),
  }), [effectiveMode, preference, setMode]);

  return <WebThemeContext.Provider value={value}>{children}</WebThemeContext.Provider>;
}

export function WebThemeProvider({ children }: { children: React.ReactNode }) {
  return (
    <ThemeProvider theme={theme} defaultMode="system" modeStorageKey={themeStorageKey} disableTransitionOnChange>
      <CssBaseline />
      <ThemeContextBridge>{children}</ThemeContextBridge>
    </ThemeProvider>
  );
}

export function useWebTheme() {
  return useContext(WebThemeContext);
}
